import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { SolitaireGame } from './SolitaireGame';
import { Card } from 'components/Card';
import { CardPlace, GamePlace } from 'constants/cards';
import { Store } from './store/gameStore';
import { GAEvent, GAEventNames } from './analytics/events';

export type MoveData = { card: Card; place: CardPlace; visible: boolean };

export class Moves {
    game: SolitaireGame;

    constructor(game: SolitaireGame) {
        this.game = game;
        this.undo = this.undo.bind(this);
        GameEvent.add(EventName.UNDO_TRIGGERED, this.undo);
    }
    removeAll() {
        Store.moves = [];
    }
    undo(skipCount = false) {
        const lastMove = Store.moves.pop();
        let repeat = false;

        if (!lastMove) return;

        this.game.blockMove(Infinity);

        if (Store.isSpider && lastMove.filter((c) => c.visible).length >= 13) {
            repeat = true;
        }
        if (!skipCount) {
            
            Store.undos++;
            GAEvent(GAEventNames.GAME_UNDO, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });

            this.removeScore(lastMove);
        }
        lastMove.sort((a, b) => {
            if (a.visible != b.visible) return a.visible ? 1 : -1;
            return 0;
        });

        lastMove.forEach((moveData) => {
            const { card, visible, place } = moveData;

            card.place = place;
            this.game.removeCardFromBoard(card);

            this.addCardToPlace(card);

            card.takeUp();
            card.moveTo(card.place.position);
            if (card.visible !== visible) card.setVisible(visible, true, card.place.cardPlace === GamePlace.STOCKPILE);
        });
        if (repeat) {
            this.undo(true);
        } else {
            this.game.blockMove(0);
            if (!skipCount){
                GameEvent.emit(EventName.UNDO_MOVE, lastMove);
            }
            const blockTime = 300;
            GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, blockTime);
            this.game.refreshBoard();
            this.game.refreshAllHighlight();
            
            if (this.game.gameStrategy) {
                this.game.gameStrategy.refreshBoardState();
                this.game.gameStrategy.handleChangeHighlight();
                this.game.onExternalClick();
            }
        }
    }

    removeScore(lastMove: MoveData[]) {
        const mainMove = lastMove[0];
        const lastMoveCard = lastMove[lastMove.length - 1];
        if (!mainMove) return;
        const currentPlace = this.mapCard(mainMove.card);
        GameEvent.emit(EventName.SUBTRACT_SCORE, { to: currentPlace.place.cardPlace, from: mainMove.place.cardPlace });

        if (lastMoveCard.place.cardPlace === GamePlace.TABLEAU && !lastMoveCard.visible) {
            GameEvent.emit(EventName.UNDO_REVEAL_CARD);
        }
    }
    didMoveToDifferentPosition(currentPlace: MoveData, oldPlace: CardPlace) {
        if (currentPlace.place.cardPlace !== oldPlace.cardPlace) return true;
        if (currentPlace.place.value !== oldPlace.value) return true;
        return false;
    }
    addCardToPlace(card: Card) {
        const { order, cardPlace, value } = card.place;
        switch (cardPlace) {
            case GamePlace.TABLEAU:
                if (this.game.gameStrategy) {
                    this.game.gameStrategy.handleInsertCardInToTable(this.game.columns[value].cards, card);
                } else {
                    this.game.columns[value].spliceCard(card, order);
                }
                break;
            case GamePlace.WASTE_PILE:
                this.game.wastePile?.spliceCard(card, order);
                break;
            case GamePlace.STOCKPILE:
                if (Store.isSpider) this.game.stockPile.putOnTop(card);
                else this.game.stockPile.spliceCard(card, order);
                break;
            case GamePlace.FOUNDATION:
                this.game.foundations[value].spliceCard(card, order);
                break;
            case GamePlace.FREECELL:
                this.game.freeCells[value].spliceCard(card, order);
                break;
        }
    }
    checkButtonState(onMagicWand = false) {
        if (!onMagicWand) {
            GameEvent.emit(
                EventName.SET_UNDO_DISABLED,
                this.game.autocompleteActivated || this.game.gameOver || Store.moves.length === 0
            );
        }
        if (this.game.autocompleteActivated || this.game.gameOver) GameEvent.emit(EventName.SET_HINT_DISABLED, true);
        if (this.game.autocompleteActivated || this.game.gameOver) GameEvent.emit(EventName.SET_WAND_DISABLED, true);
    }

    addRevealCard(card: Card) {
        const cardData = this.mapCard(card);
        cardData.visible = false;
        if (Store.moves.length === 0) {
            return;
        }
        Store.moves[Store.moves.length - 1].push(cardData);
    }

    add(cards: Card[], skipCounting = false, onMagicWand = false) {
        if (!skipCounting) GameEvent.emit(EventName.MOVE);
        const newMoveGroup = [] as MoveData[];

        cards.forEach((card: Card) => {
            newMoveGroup.push(this.mapCard(card));
        });
        if (skipCounting && Store.moves.length === 0) return;
        Store.moves.push(newMoveGroup);
        this.checkButtonState(onMagicWand);
    }
    mapCard(card: Card): MoveData {
        return { card, place: { ...card.place }, visible: card.visible };
    }
}
